import { API_BASE } from '../api';

interface ScopedTokenInfo {
  purpose: string;
  status: string;
  message?: string;
  context?: { title?: string; expires_at?: string | null };
}

interface ApiResponse<T> { status: 'ok' | 'error'; data?: T; error?: string }

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

function extractToken(): string | null {
  const m = window.location.pathname.match(/\/review\/([^/]+)/);
  return m?.[1] ?? null;
}

const PURPOSE_LABELS: Record<string, string> = {
  test_ui_review: 'Test UI Review',
  ai_review: 'AI Review',
  ndis_plan_review: 'NDIS Plan Review',
  invoice_review: 'Invoice Review',
  support_profile_setup: 'Support Profile Setup',
  support_agreement: 'Support Agreement',
  support_onboarding_combined: 'Support Onboarding',
  gitea_review: 'Code Review',
};

function purposeLabel(purpose: string): string {
  return PURPOSE_LABELS[purpose] || purpose.replace(/_/g, ' ');
}

function renderUnavailable(app: HTMLElement): void {
  app.innerHTML = `
    <div style="max-width:480px;margin:60px auto;padding:32px;background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.08);text-align:center;font-family:system-ui,-apple-system,sans-serif">
      <div style="font-size:48px;margin-bottom:16px">&#128274;</div>
      <h2 style="margin:0 0 8px;color:#1a1a2e;font-size:20px">Link Unavailable</h2>
      <p style="color:#64748b;font-size:14px;margin:0">This review link is no longer available. It may have expired, been revoked, or already been used.</p>
    </div>`;
}

function renderUsed(app: HTMLElement, info: ScopedTokenInfo): void {
  app.innerHTML = `
    <div style="max-width:480px;margin:60px auto;padding:32px;background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.08);text-align:center;font-family:system-ui,-apple-system,sans-serif">
      <div style="font-size:48px;margin-bottom:16px">&#9989;</div>
      <h2 style="margin:0 0 8px;color:#1a1a2e;font-size:20px">${purposeLabel(info.purpose)}</h2>
      <p style="color:#64748b;font-size:14px;margin:0">${info.message || 'This link has already been used.'}</p>
    </div>`;
}

function renderActive(app: HTMLElement, info: ScopedTokenInfo): void {
  const title = info.context?.title || purposeLabel(info.purpose);
  const expiryNote = info.context?.expires_at
    ? `<p style="color:#94a3b8;font-size:12px;margin:16px 0 0">Expires: ${new Date(info.context.expires_at).toLocaleString()}</p>`
    : '';

  app.innerHTML = `
    <div style="max-width:560px;margin:60px auto;padding:32px;background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.08);font-family:system-ui,-apple-system,sans-serif">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:20px">
        <span style="font-size:32px">&#128269;</span>
        <div>
          <h2 style="margin:0;color:#1a1a2e;font-size:20px">${title}</h2>
          <span style="display:inline-block;margin-top:4px;padding:2px 8px;background:#dcfce7;color:#166534;border-radius:4px;font-size:12px;font-weight:500">${purposeLabel(info.purpose)}</span>
        </div>
      </div>
      <div style="padding:16px;background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0">
        <p style="color:#475569;font-size:14px;margin:0;line-height:1.6">
          This is a scoped review link. You have read-only access to the specific resource shared with this link.
        </p>
        <p style="color:#475569;font-size:14px;margin:12px 0 0;line-height:1.6">
          This is testing only. Do not use information from this review for invoicing or billing until reviewed and confirmed by the account owner.
        </p>
      </div>
      ${expiryNote}
      <p style="color:#94a3b8;font-size:11px;margin:20px 0 0;text-align:center">
        Powered by the hub ecosystems
      </p>
    </div>`;
}

export async function renderReviewToken(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;
  app.className = 'sp-public-root';
  document.body.classList.add('sp-public-body');

  const token = extractToken();
  if (!token) { renderUnavailable(app); return; }

  try {
    const info = await apiGet<ScopedTokenInfo>(`/api/scoped-token/${encodeURIComponent(token)}/info`);
    if (info.status === 'used') {
      renderUsed(app, info);
    } else {
      renderActive(app, info);
    }
  } catch {
    renderUnavailable(app);
  }
}

export function isReviewTokenPath(): boolean {
  return /\/review\/[^/]+/.test(window.location.pathname);
}
